import React, { useCallback, useRef, useState } from 'react';
import { fetchBroadcasts, BroadcastResponse } from '../../lib/api';
import { formatAccountName } from '../../utils/formatters';
import './SendsExport.css';

interface SendsExportProps {
  accounts: string[];
  onClose: () => void;
}

/** One row per (account, New York calendar day) -- the shape the CSV writes. */
interface DayRow {
  account: string;
  date: string;
  delivered: number;
  donations: number;
  amount_raised: number;
  sb_cost: number;
  client_cost: number;
  client_roi: number;
  client_roi_percentage: number;
  model_type?: string;
  model_value?: number;
  pricing_model: string;
  uncapped_cost: number;  // cost before cost caps (= client_cost when uncapped)
}

// Column order matches the browser console script admins used before this
// screen existed, so the two CSVs are drop-in comparable. Columns added since
// go on the end so that stays true.
const FIELDS: (keyof DayRow)[] = [
  'account',
  'date',
  'delivered',
  'donations',
  'amount_raised',
  'sb_cost',
  'client_cost',
  'client_roi',
  'client_roi_percentage',
  'model_type',
  'model_value',
  'pricing_model',
  'uncapped_cost',
];

// The console script logged failures and downloaded a CSV anyway, so a missing
// account looked identical to an account with no sends. Here each account gets
// retried, and anything still failing blocks the automatic download.
const MAX_ATTEMPTS = 3;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const NY_DATE = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/New_York',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** Calendar date in America/New_York as YYYY-MM-DD.
 *  Pinned to New York so grouping matches the dashboard table regardless of
 *  the admin's browser timezone. */
function nyDate(iso: string | null): string {
  if (!iso) return '';
  const parts = NY_DATE.formatToParts(new Date(iso));
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/** Per-broadcast rows -> one row per NY calendar day.
 *
 *  Mirrors the `aggregatedData` memo in DataTable.tsx, quirks included: an
 *  roi_guarantee day reports the guaranteed percentage rather than a derived
 *  one (unless a cost cap lowered its cost -- then it's the real ROI), and
 *  profit_share caps cost at what was raised. A CSV that disagrees with the
 *  screen is worse than one that shares its assumptions.
 */
function aggregate(rows: BroadcastResponse[]): DayRow[] {
  const days = new Map<string, DayRow>();

  for (const r of rows) {
    const key = nyDate(r.created_at);
    const day = days.get(key);
    if (day) {
      day.delivered += r.delivered || 0;
      day.donations += r.donations || 0;
      day.amount_raised += r.amount_raised || 0;
      day.sb_cost += r.sb_cost || 0;
      day.client_cost += r.client_cost || 0;
      day.uncapped_cost += r.uncapped_cost ?? r.client_cost ?? 0;
    } else {
      days.set(key, {
        account: r.account,
        date: key,
        delivered: r.delivered || 0,
        donations: r.donations || 0,
        amount_raised: r.amount_raised || 0,
        sb_cost: r.sb_cost || 0,
        client_cost: r.client_cost || 0,
        client_roi: 0,
        client_roi_percentage: 0,
        model_type: r.model_type,
        model_value: r.model_value,
        pricing_model: r.pricing_model,
        uncapped_cost: r.uncapped_cost ?? r.client_cost ?? 0,
      });
    }
  }

  for (const day of Array.from(days.values())) {
    const capped = day.uncapped_cost - day.client_cost > 0.005;
    if (day.model_type === 'roi_guarantee' && (day.model_value || 0) > 0 && !capped) {
      day.client_roi_percentage = day.model_value as number;
    } else {
      if (day.model_type === 'profit_share' && day.amount_raised > 0) {
        day.client_cost = Math.min(day.client_cost, day.amount_raised);
      }
      day.client_roi_percentage =
        day.client_cost > 0 ? (day.amount_raised / day.client_cost) * 100 : 0;
    }
    day.client_roi = day.amount_raised - day.client_cost;
  }

  return Array.from(days.values()).sort((a, b) => a.date.localeCompare(b.date));
}

function escapeCell(value: unknown): string {
  if (value == null) return '';
  const s = String(value);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadCsv(rows: DayRow[]) {
  const lines = [FIELDS.join(',')];
  for (const row of rows) {
    lines.push(FIELDS.map((f) => escapeCell(row[f])).join(','));
  }
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'dashboard_sends.csv';
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export const SendsExport: React.FC<SendsExportProps> = ({ accounts, onClose }) => {
  const [running, setRunning] = useState(false);
  const [finished, setFinished] = useState(false);
  const [done, setDone] = useState(0);
  const [total, setTotal] = useState(0);
  const [current, setCurrent] = useState('');
  const [failed, setFailed] = useState<string[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [cancelled, setCancelled] = useState(false);

  // Rows accumulate across the first pass and any retry passes, so a retry
  // tops up the same CSV instead of starting over.
  const rowsRef = useRef<DayRow[]>([]);
  const cancelRef = useRef(false);

  const run = useCallback(async (list: string[], append: boolean) => {
    if (!list.length) return;

    cancelRef.current = false;
    if (!append) rowsRef.current = [];

    setRunning(true);
    setFinished(false);
    setCancelled(false);
    setFailed([]);
    setDone(0);
    setTotal(list.length);
    setRowCount(append ? rowsRef.current.length : 0);

    const stillFailed: string[] = [];

    for (let i = 0; i < list.length; i++) {
      if (cancelRef.current) break;

      const account = list[i];
      setCurrent(account);

      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        try {
          const data = await fetchBroadcasts({ account });
          for (const row of aggregate(data)) rowsRef.current.push(row);
          break;
        } catch (err) {
          if (attempt === MAX_ATTEMPTS) {
            stillFailed.push(account);
          } else {
            // Back off a little before retrying -- failures here are usually
            // a busy backend rather than a bad account.
            await sleep(1000 * attempt);
          }
        }
      }

      setDone(i + 1);
      setRowCount(rowsRef.current.length);
    }

    setCurrent('');
    setFailed(stillFailed);
    setRunning(false);
    setFinished(true);
    setCancelled(cancelRef.current);

    // Download automatically only when the run is complete. A partial CSV has
    // to be asked for, so it can never be mistaken for the full export.
    if (!cancelRef.current && stillFailed.length === 0) {
      downloadCsv(rowsRef.current);
    }
  }, []);

  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <div className="sends-export">
      <div className="sends-export-header">
        <h2>Export All Sends</h2>
        <button className="btn btn-secondary" onClick={onClose} disabled={running}>
          Back to dashboard
        </button>
      </div>

      <p className="sends-export-desc">
        Downloads every send for every account you can see, all-time, grouped by day —
        the same rows and ROI figures the dashboard table shows. Hidden day-rows are
        included. Expect this to take around 30 minutes; keep this tab open while it runs,
        though you can work in another tab.
      </p>

      {!running && !finished && (
        <button
          className="btn btn-primary"
          disabled={!accounts.length}
          onClick={() => run(accounts, false)}
        >
          Start export ({accounts.length} accounts)
        </button>
      )}

      {running && (
        <div className="sends-export-progress">
          <div className="sends-export-bar">
            <div className="sends-export-bar-fill" style={{ width: `${pct}%` }} />
          </div>
          <p className="sends-export-status">
            {done} of {total} accounts · {rowCount.toLocaleString()} rows
            {current && <> · fetching {formatAccountName(current)}</>}
          </p>
          <button className="btn btn-secondary" onClick={() => { cancelRef.current = true; }}>
            Stop
          </button>
        </div>
      )}

      {finished && (
        <div className="sends-export-result">
          {cancelled && (
            <p className="sends-export-warn">
              Stopped early — {done} of {total} accounts fetched.
            </p>
          )}

          {!cancelled && failed.length === 0 && (
            <p className="sends-export-success">
              Done: {rowCount.toLocaleString()} rows from {total} accounts. Download started
              as dashboard_sends.csv
            </p>
          )}

          {failed.length > 0 && (
            <>
              <p className="sends-export-warn">
                {failed.length} account{failed.length === 1 ? '' : 's'} failed after{' '}
                {MAX_ATTEMPTS} attempts and {failed.length === 1 ? 'is' : 'are'} missing from
                the data. Retry before downloading.
              </p>
              <ul className="sends-export-failed">
                {failed.map((a) => (
                  <li key={a}>{formatAccountName(a)}</li>
                ))}
              </ul>
            </>
          )}

          <div className="sends-export-actions">
            {failed.length > 0 && (
              <button className="btn btn-primary" onClick={() => run(failed, true)}>
                Retry {failed.length} failed
              </button>
            )}
            <button
              className={failed.length > 0 ? 'btn btn-secondary' : 'btn btn-primary'}
              disabled={rowCount === 0}
              onClick={() => downloadCsv(rowsRef.current)}
            >
              {failed.length > 0 || cancelled ? 'Download anyway' : 'Download again'}
            </button>
            <button className="btn btn-secondary" onClick={() => run(accounts, false)}>
              Start over
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
